import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 30 seconds or on second visit
      const visitCount = parseInt(localStorage.getItem('maks_visit_count') || '0') + 1;
      localStorage.setItem('maks_visit_count', visitCount.toString());
      
      const dismissed = localStorage.getItem('maks_pwa_dismissed');
      if (!dismissed && visitCount >= 2) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check for iOS - show custom instructions
    if (isIOSDevice) {
      const dismissed = localStorage.getItem('maks_pwa_dismissed');
      const visitCount = parseInt(localStorage.getItem('maks_visit_count') || '0') + 1;
      localStorage.setItem('maks_visit_count', visitCount.toString());
      
      if (!dismissed && visitCount >= 2) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('maks_pwa_dismissed', 'true');
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom-4">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-green-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
          M
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 mb-1">Install MAKS Booking</h3>
          <p className="text-sm text-slate-600 mb-3">
            {isIOS 
              ? "Tap the share button and select 'Add to Home Screen' for quick access."
              : "Install the app for faster access and offline support."
            }
          </p>
          
          {isIOS ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Smartphone className="w-4 h-4" />
              <span>Share → Add to Home Screen</span>
            </div>
          ) : (
            <Button
              onClick={handleInstall}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Install App
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
